import {
  ToggleButton as MuiToggleButton,
  ToggleButtonGroup as MuiToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { ReactNode } from 'react';

import { useColors } from '../ThemeProvider';

interface ToggleButtonOption<T extends string | number> {
  label: string;
  value: T;
  icon?: ReactNode;
  tooltip?: string;
}

interface Props<T extends string | number> {
  options: ToggleButtonOption<T>[];
  value: T | null;
  onChange: (value: T | null) => void;
  exclusive?: boolean;
}

export function ToggleButtonGroup<T extends string | number>({
  options,
  value,
  onChange,
  exclusive = true,
}: Props<T>) {
  const { border, primary, surfaceHover, textPrimary } = useColors();

  return (
    <MuiToggleButtonGroup
      value={value}
      exclusive={exclusive}
      onChange={(_, newValue) => onChange(newValue)}
      sx={{
        '& .MuiToggleButton-root': {
          textTransform: 'none',
          padding: '0.5rem 1rem',
          color: textPrimary,
          borderColor: border,
          '&:hover': {
            backgroundColor: surfaceHover,
          },
          '&.Mui-selected': {
            fontWeight: 500,
            backgroundColor: alpha(primary, 0.1),
            borderColor: alpha(primary, 0.6),
            color: textPrimary,
          },
          '&.Mui-selected:hover': {
            backgroundColor: alpha(primary, 0.15),
          },
        },
      }}
    >
      {options.map((option) => {
        const button = (
          <MuiToggleButton key={option.value} value={option.value}>
            {option.label}
          </MuiToggleButton>
        );
        return option.tooltip ? (
          <Tooltip describeChild key={option.value} title={option.tooltip}>
            {button}
          </Tooltip>
        ) : (
          button
        );
      })}
    </MuiToggleButtonGroup>
  );
}
